import React, { useEffect, useMemo, useRef, useState } from "react";
import { Grid } from "@mui/material";
import { FormButton, SpinnerOverlay } from "components";
import {
  FormikDateInput,
  FormikSelectInput,
  FormikTextInput,
} from "components/UI/Inputs/FormikInputs";
import { Form, Formik } from "formik";
import * as yup from "yup";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { snackbarData, userCredentials } from "recoilState/GlobalState";
import { mensajeLargo } from "consts/limites-campos";
import {
  ASIGNACIONES,
  LARGO_AGENTE,
  MATRICULA_NO_APLICA,
  PUEDE_GUARDAR_ASIGNACION,
  TIPOS_HORARIO,
  requiereMatricula,
} from "../consts";
import { RNHTituloGet } from "services/services-mia/programa-salud/personal/RNHTituloGet";
import { RNHCargoGet } from "services/services-mia/programa-salud/personal/RNHCargoGet";
import { RNHConvenioGet } from "services/services-mia/programa-salud/personal/RNHConvenioGet";
import { RNHEstablecimientoGet } from "services/services-mia/programa-salud/estructuras/RNHEstablecimientoGet";
import { RNHAgenteUpd } from "services/services-mia/programa-salud/personal/RNHAgenteUpd";
import styles from "./LaboralesModal.module.scss";

/**
 * Edición de los datos LABORALES del agente, desde la vista de detalle.
 *
 * No reusa el modal del alta a propósito: aquel arranca por una búsqueda en MIA y
 * arrastra toda la columna de datos personales, que acá no se tocan. Este modal es
 * solo el vínculo laboral, con el mismo orden de campos que el alta para que quien
 * los cargó los vuelva a encontrar donde estaban.
 *
 * La precarga sale de `datosAgente` (el RNHAgenteGet por id que ya hizo el detalle) y
 * no de la fila del listado: la fila no trae `rnhAgenteVersion`, `usuarioMail` ni el
 * título, que son justo lo que el Upd necesita.
 *
 * PARA `RNHAgenteUpd`, OMITIR UN CAMPO ES "NO CAMBIAR" Y LA CADENA VACIA SE GRABA. Por
 * eso el servicio manda todo con `|| null` y la version con `??` —la de un registro
 * recien creado es 0—. Consecuencia practica: desde aca no se puede VACIAR un campo,
 * solo cambiarlo.
 *
 * EL HOSPITAL CAMBIA DE NOMBRE ENTRE IDA Y VUELTA: el param es `rnhHospitalCod` y en
 * la response llega como `rnhEstablecimientoCod`. Los params nombran la clave foranea
 * por la tabla que la tiene, las responses por la tabla a la que apuntan.
 *
 * Una matricula sin titulo se rechaza (41021), asi que no viaja sola.
 */
export default function LaboralesModal({
  row,
  datosAgente,
  updateList,
  closeModal,
}) {
  const setSnackBarInfo = useSetRecoilState(snackbarData);
  const credentials = useRecoilValue(userCredentials);
  const formikRef = useRef();

  const [titulos, setTitulos] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [convenios, setConvenios] = useState([]);
  const [hospitales, setHospitales] = useState([]);

  const [inRequest, setInRequest] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const getTitulos = async () => {
    const { data: result } = await RNHTituloGet(credentials);
    setTitulos(result?.[0]?.rnhTituloId ? result : []);
  };
  const getCargos = async () => {
    const { data: result } = await RNHCargoGet(credentials);
    setCargos(result?.[0]?.rnhCargoId ? result : []);
  };
  const getConvenios = async () => {
    const { data: result } = await RNHConvenioGet(credentials);
    setConvenios(result?.[0]?.rnhConvenioId ? result : []);
  };
  const getHospitales = async () => {
    const { data: result } = await RNHEstablecimientoGet(credentials, { tipo: "H" });
    setHospitales(result?.[0]?.rnhEstablecimientoCod ? result : []);
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await Promise.all([
        getTitulos(),
        getCargos(),
        getConvenios(),
        getHospitales(),
      ]);
      setIsLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const opcionesHospital = useMemo(() => {
    const actual = datosAgente?.rnhEstablecimientoCod;
    if (!actual) return hospitales;

    const yaEsta = hospitales.some(
      (h) => String(h.rnhEstablecimientoCod) === String(actual)
    );
    if (yaEsta) return hospitales;

    return [
      {
        rnhEstablecimientoCod: actual,
        rnhEstablecimientoNombre: datosAgente?.rnhEstablecimientoNombre || actual,
      },
      ...hospitales,
    ];
  }, [hospitales, datosAgente]);

  const guardarLaborales = async (values) => {
    setInRequest(true);

    const response = await RNHAgenteUpd(credentials, {
      agenteId: datosAgente?.rnhAgenteId ?? row?.id,
      version: datosAgente?.rnhAgenteVersion,
      mail: datosAgente?.usuarioMail,
      hospitalCod: values.hospitalCod,
      legajo: values.legajo,
      fechaInicio: values.fechaInicio,
      convenioCod: values.convenioCod,
      horarioTipo: values.horarioTipo,
      tituloCod: values.tituloCod,
      matricula: values.tituloCod ? values.matricula : null,
      cargoCod: values.cargoCod,
      esUnico: values.unique,
    });
    setInRequest(false);

    if (!response.ok) return;

    setSnackBarInfo({
      message: "Datos laborales modificados correctamente",
      severity: "success",
      open: true,
    });
    updateList();
    closeModal();
  };

  const validationSchema = yup.object({
    hospitalCod: yup.string(),
    legajo: yup.string().max(LARGO_AGENTE.legajo, mensajeLargo(LARGO_AGENTE.legajo)),
    convenioCod: yup.string(),
    horarioTipo: yup.string(),
    tituloCod: yup.string(),
    fechaInicio: yup.string(),
    matricula: yup
      .string()
      .max(LARGO_AGENTE.matricula, mensajeLargo(LARGO_AGENTE.matricula))
      .when("tituloCod", {
        is: (tituloCod) => requiereMatricula(titulos, tituloCod),
        then: yup.string().required("El título seleccionado requiere matrícula"),
        otherwise: yup.string(),
      }),
    cargoCod: yup.string(),
    unique: yup.string(),
  });

  return (
    <Formik
      innerRef={formikRef}
      initialValues={{
        hospitalCod: datosAgente?.rnhEstablecimientoCod
          ? String(datosAgente.rnhEstablecimientoCod)
          : "",
        legajo: datosAgente?.rnhAgenteLegajo ?? "",
        convenioCod: datosAgente?.rnhConvenioCod ?? "",
        horarioTipo: datosAgente?.rnhAgenteHorarioTipo ?? "",
        tituloCod: datosAgente?.rnhTituloCod ?? "",
        fechaInicio: datosAgente?.rnhAgenteFechaInicio ?? "",
        matricula: datosAgente?.rnhAgenteMatricula || MATRICULA_NO_APLICA,
        cargoCod: datosAgente?.rnhCargoCod ?? "",
        unique: datosAgente?.rnhAgenteEstablecimientoUnico ?? "",
      }}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        guardarLaborales(values);
      }}
    >
      {({ values }) => {
        const pideMatricula = requiereMatricula(titulos, values.tituloCod);

        return (
          <Form className={styles.contenedor} noValidate>
            <Grid
              container
              rowSpacing={0}
              columnSpacing={2}
              sx={{
                "& .MuiFormControl-root": { marginTop: 0, marginBottom: 0 },
              }}
            >
              <Grid item xs={12}>
                <FormikSelectInput
                  name="hospitalCod"
                  labelText="Establecimiento"
                  disabled={isLoading}
                >
                  <option value="" disabled>
                    Seleccione
                  </option>
                  {opcionesHospital.map(
                    ({ rnhEstablecimientoCod, rnhEstablecimientoNombre }) => (
                      <option
                        key={rnhEstablecimientoCod}
                        value={String(rnhEstablecimientoCod)}
                      >
                        {rnhEstablecimientoNombre}
                      </option>
                    )
                  )}
                </FormikSelectInput>
              </Grid>

              <Grid item xs={6}>
                <FormikTextInput
                  name="legajo"
                  inputProps={{ maxLength: LARGO_AGENTE.legajo }}
                  labelText="Legajo"
                  disabled={isLoading}
                />
              </Grid>
              <Grid item xs={3}>
                <FormikSelectInput
                  name="convenioCod"
                  labelText="Régimen Horario"
                  disabled={isLoading}
                >
                  <option value="">Sin asignar</option>
                  {convenios.map(({ rnhConvenioCod, rnhConvenioLey, rnhConvenioCargaSemanal }) => (
                    <option key={rnhConvenioCod} value={rnhConvenioCod}>
                      {[rnhConvenioLey && `Ley ${rnhConvenioLey}, ${rnhConvenioCargaSemanal}hs`]
                        .filter(Boolean)
                        .join(" · ")}
                    </option>
                  ))}
                </FormikSelectInput>
              </Grid>
              <Grid item xs={3}>
                <FormikSelectInput
                  name="horarioTipo"
                  labelText="Tipo de Horario"
                  disabled={isLoading}
                >
                  <option value="">Sin asignar</option>
                  {TIPOS_HORARIO.map(({ cod, label }) => (
                    <option key={cod} value={cod}>
                      {label}
                    </option>
                  ))}
                </FormikSelectInput>
              </Grid>
              <Grid item xs={6}>
                <FormikSelectInput
                  name="cargoCod"
                  labelText="Cargo"
                  disabled={isLoading}
                >
                  <option value="" disabled>
                    Seleccione
                  </option>
                  {cargos.map(({ rnhCargoCod, rnhCargoNombre }) => (
                    <option key={rnhCargoCod} value={rnhCargoCod}>
                      {rnhCargoNombre}
                    </option>
                  ))}
                </FormikSelectInput>
              </Grid>
              <Grid item xs={6}>
                <FormikDateInput
                  name="fechaInicio"
                  labelText="Fecha de inicio"
                  disabled={isLoading}
                />
              </Grid>

              <Grid item xs={6}>
                <FormikSelectInput
                  name="tituloCod"
                  labelText="Título"
                  disabled={isLoading}
                  onChange={(e) => {
                    const tituloCod = e.target.value;
                    formikRef.current.setFieldValue("tituloCod", tituloCod);
                    formikRef.current.setFieldValue(
                      "matricula",
                      requiereMatricula(titulos, tituloCod) ? "" : MATRICULA_NO_APLICA
                    );
                  }}
                >
                  <option value="">Sin asignar</option>
                  {titulos.map(({ rnhTituloCod, rnhTituloNombre }) => (
                    <option key={rnhTituloCod} value={rnhTituloCod}>
                      {rnhTituloNombre}
                    </option>
                  ))}
                </FormikSelectInput>
              </Grid>
              <Grid item xs={3}>
                <FormikTextInput
                  name="matricula"
                  inputProps={{ maxLength: LARGO_AGENTE.matricula }}
                  labelText="Matrícula"
                  helperText={
                    !pideMatricula && values.tituloCod
                      ? "No requerida para el título"
                      : undefined
                  }
                  disabled={isLoading || !pideMatricula}
                />
              </Grid>
              <Grid item xs={3}>
                <FormikSelectInput
                  name="unique"
                  labelText="Asignación"
                  helperText={
                    PUEDE_GUARDAR_ASIGNACION
                      ? undefined
                      : "El servicio todavía no guarda este campo"
                  }
                  disabled={isLoading}
                >
                  <option value="">Sin asignar</option>
                  {ASIGNACIONES.map(({ cod, label }) => (
                    <option key={cod} value={cod}>
                      {label}
                    </option>
                  ))}
                </FormikSelectInput>
              </Grid>
            </Grid>

            <FormButton
              inRequest={inRequest || isLoading}
              newFormData={{ button: "Modificar" }}
              onCloseModal={closeModal}
            />
            {(inRequest || isLoading) && <SpinnerOverlay />}
          </Form>
        );
      }}
    </Formik>
  );
}
